/**
 * Kỳ báo cáo BLD phía client (ngày / tuần / tháng / quý) cho bộ chọn kỳ ở "Chạy test nhanh".
 *
 * Logic thuần trên chuỗi `YYYY-MM-DD` (tính bằng UTC, không phụ thuộc múi giờ máy). Tuần chạy
 * thứ 2 → Chủ nhật (ISO) giống server (`period-rules.ts`). Server cần `periodEnd` của kỳ đã chọn.
 */
import type { BldPeriodType } from '@/lib/bld-reports-history-api';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface BldPeriodSelection {
  periodType: BldPeriodType;
  periodStart: string;
  periodEnd: string;
  label: string;
}

export interface BldDayCell {
  date: string;
  inMonth: boolean;
}

export interface BldWeekOfMonth {
  /** Số thứ tự tuần trong tháng, bắt đầu từ 1 (tuần chứa ngày 1 là Tuần 1). */
  index: number;
  start: string;
  end: string;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function toUtc(dateOnly: string): number {
  const [year = 0, month = 1, day = 1] = dateOnly.split('-').map((part) => Number.parseInt(part, 10));
  return Date.UTC(year, month - 1, day);
}

function fromUtc(ms: number): string {
  const date = new Date(ms);
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

function shiftDate(dateOnly: string, days: number): string {
  return fromUtc(toUtc(dateOnly) + days * MS_PER_DAY);
}

/** Số ngày lùi từ `dateOnly` về thứ 2 (thứ 2 = 0 … Chủ nhật = 6). */
function daysBackToMonday(dateOnly: string): number {
  return (new Date(toUtc(dateOnly)).getUTCDay() + 6) % 7;
}

function startOfIsoWeek(dateOnly: string): string {
  return shiftDate(dateOnly, -daysBackToMonday(dateOnly));
}

function lastDayOfMonth(year: number, month: number): string {
  return fromUtc(Date.UTC(year, month, 0));
}

function formatDmy(dateOnly: string): string {
  const [year, month, day] = dateOnly.split('-');
  return `${day}/${month}/${year}`;
}

/** Lưới lịch tháng 6 hàng x 7 cột (thứ 2 đầu hàng), kèm ngày của tháng liền kề. */
export function getMonthDayGrid(year: number, month: number): BldDayCell[] {
  const first = `${year}-${pad(month)}-01`;
  const gridStart = startOfIsoWeek(first);
  return Array.from({ length: 42 }, (_, offset) => {
    const date = shiftDate(gridStart, offset);
    return { date, inMonth: date.startsWith(`${year}-${pad(month)}-`) };
  });
}

/** Các tuần (thứ 2 → Chủ nhật) chạm vào tháng, kể cả tuần vắt sang tháng liền kề. */
export function getMonthWeeks(year: number, month: number): BldWeekOfMonth[] {
  const lastDay = lastDayOfMonth(year, month);
  const weeks: BldWeekOfMonth[] = [];
  let start = startOfIsoWeek(`${year}-${pad(month)}-01`);
  while (start <= lastDay) {
    weeks.push({ index: weeks.length + 1, start, end: shiftDate(start, 6) });
    start = shiftDate(start, 7);
  }
  return weeks;
}

export function makeDaySelection(date: string): BldPeriodSelection {
  return { periodType: 'day', periodStart: date, periodEnd: date, label: formatDmy(date) };
}

export function makeWeekSelection(weekStart: string, indexInMonth: number): BldPeriodSelection {
  const periodEnd = shiftDate(weekStart, 6);
  return {
    periodType: 'week',
    periodStart: weekStart,
    periodEnd,
    label: `Tuần ${indexInMonth} (${formatDmy(weekStart)} - ${formatDmy(periodEnd)})`,
  };
}

export function makeMonthSelection(year: number, month: number): BldPeriodSelection {
  return {
    periodType: 'month',
    periodStart: `${year}-${pad(month)}-01`,
    periodEnd: lastDayOfMonth(year, month),
    label: `Tháng ${month}/${year}`,
  };
}

export function makeQuarterSelection(year: number, quarter: number): BldPeriodSelection {
  const startMonth = (quarter - 1) * 3 + 1;
  return {
    periodType: 'quarter',
    periodStart: `${year}-${pad(startMonth)}-01`,
    periodEnd: lastDayOfMonth(year, startMonth + 2),
    label: `Quý ${quarter}/${year}`,
  };
}

/** Kỳ chứa `today` cho từng loại (mặc định khi mở popup / đổi loại kỳ). */
export function getDefaultPeriodSelection(
  periodType: BldPeriodType,
  today: string,
): BldPeriodSelection {
  const year = Number.parseInt(today.slice(0, 4), 10);
  const month = Number.parseInt(today.slice(5, 7), 10);
  switch (periodType) {
    case 'day':
      return makeDaySelection(today);
    case 'week': {
      const week = getMonthWeeks(year, month).find(
        (item) => item.start <= today && today <= item.end,
      );
      // `today` luôn nằm trong một tuần của tháng chứa nó; fallback chỉ để thỏa kiểu.
      return week ? makeWeekSelection(week.start, week.index) : makeWeekSelection(startOfIsoWeek(today), 1);
    }
    case 'month':
      return makeMonthSelection(year, month);
    case 'quarter':
      return makeQuarterSelection(year, Math.ceil(month / 3));
  }
}

/** Kỳ bắt đầu sau hôm nay thì chưa có dữ liệu → bị vô hiệu hóa trong bộ chọn. */
export function isPeriodInFuture(periodStart: string, today: string): boolean {
  return periodStart > today;
}
